import { useCallback, useRef, useState } from 'react';

export type UseDisclosureProps = {
  /** Controlled open state. When provided, the hook holds no state of its own. */
  isOpen?: boolean;
  /** Initial open state in uncontrolled mode. Ignored when `isOpen` is given. */
  defaultIsOpen?: boolean;
  /** Called by `onOpen`/`onToggle` in BOTH controlled and uncontrolled mode. */
  onOpen?: () => void;
  /** Called by `onClose`/`onToggle` in BOTH controlled and uncontrolled mode. */
  onClose?: () => void;
};

export type UseDisclosureReturn = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onToggle: () => void;
  /** True when the caller drives `isOpen`. */
  isControlled: boolean;
};

/**
 * Boolean open/close state for modals, popovers, menus and drawers.
 *
 * Drop-in for Chakra's `useDisclosure` across the surface this repo uses:
 * the `isOpen` / `onOpen` / `onClose` / `onToggle` / `isControlled` return and
 * the `isOpen` / `defaultIsOpen` / `onOpen` / `onClose` props, with the same
 * controlled/uncontrolled semantics.
 *
 * Deliberately omits Chakra's `getButtonProps` / `getDisclosureProps` (and the
 * `id` prop that only existed to feed them): nothing in the repo calls them,
 * and the components that need trigger wiring — `Menu`, `RevelioTooltip` — own
 * their own aria plumbing via Radix. Add them only if a real caller appears.
 *
 * The `onOpen`/`onClose` callbacks are read through a ref, so passing inline
 * arrow functions does not change the identity of the returned handlers.
 */
export const useDisclosure = ({
  isOpen: isOpenProp,
  defaultIsOpen = false,
  onOpen: onOpenProp,
  onClose: onCloseProp,
}: UseDisclosureProps = {}): UseDisclosureReturn => {
  const [isOpenState, setIsOpenState] = useState(defaultIsOpen);

  const isControlled = isOpenProp !== undefined;
  const isOpen = isControlled ? isOpenProp : isOpenState;

  // Keep the latest callbacks without making them dependencies: callers pass
  // inline closures, and a changing `onOpen` identity would otherwise churn
  // every consumer that lists these handlers in a dependency array.
  const callbacksRef = useRef({ onOpen: onOpenProp, onClose: onCloseProp });
  callbacksRef.current = { onOpen: onOpenProp, onClose: onCloseProp };

  const onOpen = useCallback(() => {
    if (!isControlled) {
      setIsOpenState(true);
    }
    callbacksRef.current.onOpen?.();
  }, [isControlled]);

  const onClose = useCallback(() => {
    if (!isControlled) {
      setIsOpenState(false);
    }
    callbacksRef.current.onClose?.();
  }, [isControlled]);

  const onToggle = useCallback(() => {
    if (isOpen) {
      onClose();
    } else {
      onOpen();
    }
  }, [isOpen, onOpen, onClose]);

  return { isOpen, onOpen, onClose, onToggle, isControlled };
};
